import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { getUserProfile } from '@lendica/auth';
import type { User } from '@lendica/utils';

type UserContextValue = {
    user: User | null;
    loading: boolean;
    error: unknown;
    initialized: boolean;
    refreshUser: (options?: { force?: boolean }) => Promise<User | null>;
};

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const fetchUser = useCallback(async () => {
        const profile = await getUserProfile();
        return profile ?? null;
    }, []);

    const refreshUser = useCallback(
        async (options?: { force?: boolean }) => {
            const force = options?.force ?? false;
            if (!force && hasLoaded) {
                return user;
            }

            if (mountedRef.current) {
                setLoading(true);
                setError(null);
            }

            try {
                const profile = await fetchUser();
                if (mountedRef.current) {
                    setUser(profile);
                }
                return profile;
            } catch (err) {
                if (mountedRef.current) {
                    setUser(null);
                    setError(err);
                }
                throw err;
            } finally {
                if (mountedRef.current) {
                    setLoading(false);
                    setHasLoaded(true);
                }
            }
        },
        [fetchUser, hasLoaded, user]
    );

    useEffect(() => {
        if (hasLoaded) return;
        refreshUser({ force: true }).catch(() => undefined);
    }, [hasLoaded, refreshUser]);

    const value = useMemo(
        () => ({
            user,
            loading,
            error,
            initialized: hasLoaded,
            refreshUser,
        }),
        [user, loading, error, hasLoaded, refreshUser]
    );

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUserContext() {
    const ctx = useContext(UserContext);
    if (!ctx) throw new Error('UserContext not available');
    return ctx;
}

export function useUser() {
    return useUserContext().user;
}

export function useUserLoading() {
    const { loading, initialized } = useUserContext();
    return { loading, initialized };
}

export function useRefreshUser() {
    return useUserContext().refreshUser;
}
